import { useState, useEffect, useRef } from 'react';
import { Loader2, Search, Send, X, Clock, MessageSquare } from 'lucide-react';
import { questionnairesApi } from '@/api/endpoints/questionnaires.api';
import { patientsApi } from '@/api/endpoints/patients.api';
import type { QuestionnaireDefinition, DeliveryChannel } from '@/types/questionnaire.types';
import type { PatientListResponse } from '@/types/patient.types';
import { DeliveryChannelLabels } from '@/types/questionnaire.types';
import { maskCPF } from '@/lib/utils';

interface SendQuestionnaireDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId?: string;
  patientName?: string;
  onSuccess: () => void;
}

export function SendQuestionnaireDialog({
  open,
  onOpenChange,
  patientId: initialPatientId,
  patientName: initialPatientName,
  onSuccess,
}: SendQuestionnaireDialogProps) {
  const [definitions, setDefinitions] = useState<QuestionnaireDefinition[]>([]);
  const [loadingDefinitions, setLoadingDefinitions] = useState(true);

  // Patient search
  const [searchTerm, setSearchTerm] = useState(initialPatientName || '');
  const [patients, setPatients] = useState<PatientListResponse[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<PatientListResponse | null>(
    initialPatientId && initialPatientName
      ? { id: initialPatientId, name: initialPatientName } as PatientListResponse
      : null
  );
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Form state
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState('');
  const [channel, setChannel] = useState<DeliveryChannel>('WHATSAPP');
  const [scheduledAt, setScheduledAt] = useState('');
  const [expirationDays, setExpirationDays] = useState(7);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadDefinitions();
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const loadDefinitions = async () => {
    try {
      setLoadingDefinitions(true);
      const defs = await questionnairesApi.getDefinitions();
      setDefinitions(defs.filter(d => d.active));
    } catch (err) {
      console.error('Erro ao carregar questionários:', err);
    } finally {
      setLoadingDefinitions(false);
    }
  };

  const searchPatients = async (term: string) => {
    if (term.length < 2) {
      setPatients([]);
      return;
    }

    try {
      setSearching(true);
      const response = await patientsApi.getAll({ search: term, page: 0, size: 10 });
      setPatients(response.content);
    } catch (err) {
      console.error('Erro ao buscar pacientes:', err);
    } finally {
      setSearching(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setSelectedPatient(null);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        searchPatients(value);
      }, 300);
    } else {
      setPatients([]);
    }
  };

  const handleSelectPatient = (patient: PatientListResponse) => {
    setSelectedPatient(patient);
    setSearchTerm(patient.name);
    setPatients([]);
  };

  const handleSend = async () => {
    if (!selectedPatient) {
      setError('Selecione um paciente');
      return;
    }
    if (!selectedQuestionnaire) {
      setError('Selecione um questionário');
      return;
    }

    try {
      setSending(true);
      setError(null);

      await questionnairesApi.sendQuestionnaire({
        patientId: selectedPatient.id,
        questionnaireCode: selectedQuestionnaire,
        channel,
        scheduledAt: scheduledAt || undefined,
        expirationDays,
      });

      onSuccess();
      handleClose();
    } catch (err: unknown) {
      console.error('Erro ao enviar questionário:', err);
      const errorMessage =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      setError(errorMessage || 'Erro ao enviar questionário');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (!initialPatientId) {
      setSearchTerm('');
      setSelectedPatient(null);
    }
    setPatients([]);
    setSelectedQuestionnaire('');
    setChannel('WHATSAPP');
    setScheduledAt('');
    setExpirationDays(7);
    setError(null);
    onOpenChange(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Enviar Questionário</h2>
          <button
            onClick={handleClose}
            className="rounded-lg p-1 hover:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {/* Patient Search */}
        {!initialPatientId ? (
          <div className="mb-4 relative">
            <label className="mb-1 block text-sm font-medium">Paciente *</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Buscar por nome ou CPF..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full rounded-lg border bg-background py-2 pl-10 pr-4"
              />
              {searching && (
                <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin" />
              )}
            </div>

            {patients.length > 0 && !selectedPatient && (
              <div className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-lg border bg-background shadow-lg">
                {patients.map((patient) => (
                  <button
                    key={patient.id}
                    onClick={() => handleSelectPatient(patient)}
                    className="w-full px-4 py-2 text-left hover:bg-muted"
                  >
                    <p className="font-medium">{patient.name}</p>
                    <p className="text-sm text-muted-foreground">{maskCPF(patient.cpf)}</p>
                  </button>
                ))}
              </div>
            )}

            {selectedPatient && (
              <p className="mt-1 text-sm text-green-600">
                Paciente selecionado: {selectedPatient.name}
              </p>
            )}
          </div>
        ) : (
          <div className="mb-4">
            <label className="mb-1 block text-sm font-medium">Paciente</label>
            <p className="rounded-lg border bg-muted/50 p-2 text-sm">
              {initialPatientName}
            </p>
          </div>
        )}

        {/* Questionnaire Selection */}
        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium">Questionário *</label>
          {loadingDefinitions ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Carregando...
            </div>
          ) : (
            <select
              value={selectedQuestionnaire}
              onChange={(e) => setSelectedQuestionnaire(e.target.value)}
              className="w-full rounded-lg border bg-background p-2"
            >
              <option value="">Selecione um questionário</option>
              {definitions.map((def) => (
                <option key={def.code} value={def.code}>
                  {def.name} ({def.questionCount} perguntas
                  {def.estimatedMinutes && `, ~${def.estimatedMinutes} min`})
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Channel */}
        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium">Canal de Envio</label>
          <div className="flex gap-2">
            {(['WHATSAPP', 'EMAIL', 'PORTAL'] as DeliveryChannel[]).map((ch) => (
              <button
                key={ch}
                onClick={() => setChannel(ch)}
                className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm transition-colors ${
                  channel === ch
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'hover:bg-muted'
                }`}
              >
                <MessageSquare className="h-4 w-4" />
                {DeliveryChannelLabels[ch]}
              </button>
            ))}
          </div>
        </div>

        {/* Scheduled At */}
        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium">
            <Clock className="mr-1 inline h-4 w-4" />
            Agendar para (opcional)
          </label>
          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            className="w-full rounded-lg border bg-background p-2"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Deixe em branco para enviar imediatamente
          </p>
        </div>

        {/* Expiration */}
        <div className="mb-6">
          <label className="mb-1 block text-sm font-medium">Validade (dias)</label>
          <select
            value={expirationDays}
            onChange={(e) => setExpirationDays(Number(e.target.value))}
            className="w-full rounded-lg border bg-background p-2"
          >
            <option value={3}>3 dias</option>
            <option value={7}>7 dias</option>
            <option value={14}>14 dias</option>
            <option value={30}>30 dias</option>
          </select>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={handleClose}
            className="rounded-lg border px-4 py-2 hover:bg-muted"
            disabled={sending}
          >
            Cancelar
          </button>
          <button
            onClick={handleSend}
            disabled={!selectedPatient || !selectedQuestionnaire || sending}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {scheduledAt ? 'Agendar' : 'Enviar'}
          </button>
        </div>
      </div>
    </div>
  );
}
